import { objectIdSchema, type LeadStatus, type ReferralAttribution } from '@hero/schema';
import { z } from 'zod';
import { API } from '../..';

/* ─────────────────────────── admin (PostHog) ─────────────────────────── */

/**
 * Look-back window in days. `0` = all time. Bounded because the value
 * is inlined into HogQL.
 */
export const referralAnalyticsDaysSchema = z.coerce
  .number()
  .int()
  .min(0)
  .max(365)
  .default(30)
  .describe('Look-back window in days; 0 = all time. Default 30.');

/**
 * Scope shared by the PostHog-backed referral analytics endpoints.
 * Exactly one of `linkId` / `partnerId` is required. The backend turns
 * it into the set of **people the scope owns** — the CRM leads whose
 * `meta.sourceMeta.referral` points at the partner / link — and reads
 * their PostHog activity. Ownership lives in our DB; PostHog never
 * decides who belongs to whom.
 */
export const referralAnalyticsScopeSchema = z
  .object({
    linkId: objectIdSchema.optional().describe('Scope to one link.'),
    partnerId: objectIdSchema.optional().describe('Scope to every link of a partner.'),
    days: referralAnalyticsDaysSchema,
  })
  .refine(v => !!v.linkId || !!v.partnerId, {
    message: 'linkId or partnerId is required',
    path: ['linkId'],
  });

/**
 * Whether PostHog querying is configured on this backend. Every
 * analytics response carries it so the admin panel can show "connect
 * PostHog" instead of an empty chart.
 */
export type ReferralAnalyticsMeta = {
  configured: boolean;
  /** The codes the scope covers (for the PostHog deep link). */
  codes: string[];
  /** `0` = all time. */
  days: number;
  /** How many people the scope owns (CRM leads), regardless of PostHog. */
  people: number;
  /** `true` when PostHog was configured but the query failed / timed out; numbers are missing, not zero. */
  posthogError?: boolean;
  /** Deep link to the PostHog project's activity view filtered to the codes. */
  posthogUrl?: string;
};

/**
 * `GET /referral/analytics/admin-activity` — what the people this
 * scope owns did in PostHog, in the window: per-event counts and a
 * daily series. Only owned people count — nobody who merely opened a
 * link.
 */
export const referralAnalyticsActivityAdminAPISchema = referralAnalyticsScopeSchema;
export type ReferralAnalyticsEventRow = {
  event: string;
  count: number;
  /** Distinct people. */
  persons: number;
};
export type ReferralAnalyticsDailyRow = {
  /** `YYYY-MM-DD`. */
  day: string;
  events: number;
  persons: number;
};
export type ReferralAnalyticsActivityAdminAPI = API<
  z.infer<typeof referralAnalyticsActivityAdminAPISchema>,
  {
    meta: ReferralAnalyticsMeta;
    events: ReferralAnalyticsEventRow[];
    daily: ReferralAnalyticsDailyRow[];
    /** `persons` = owned people with at least one event in the window. */
    totals: { events: number; persons: number };
  }
>;

/**
 * `GET /referral/analytics/admin-people` — the people this scope owns
 * (one row per CRM lead, newest attribution first), each with the link
 * they came through, the first code they ever saw when it differs, and
 * their PostHog footprint in the window (when PostHog knows them).
 */
export const referralAnalyticsPeopleAdminAPISchema = referralAnalyticsScopeSchema;
export type ReferralAnalyticsPerson = {
  leadId: string;
  name?: string;
  phone?: string;
  /** The lead has an Org / User on `parties[]`. */
  hasAccount: boolean;
  /** The Org on `parties[]`, when there is one — for the "open org" link. */
  org?: { _id: string; name: string };
  status: LeadStatus;
  /** The code that owns the person — the link they signed up / wrote through. */
  code: string;
  linkId: string;
  /** The first code the person ever saw, when it differs from `code`. History only. */
  originalCode?: string;
  mode: ReferralAttribution['mode'];
  /** When the attribution was recorded. */
  attributedAt: string;
  /** PostHog person id (UUID) when PostHog knows one of the person's distinct ids. */
  personId?: string;
  /** Events in the window; `0` also when PostHog has never seen them. */
  events: number;
  lastSeen?: string;
  /** Deep link to the person in PostHog. */
  posthogUrl?: string;
};
export type ReferralAnalyticsPeopleAdminAPI = API<
  z.infer<typeof referralAnalyticsPeopleAdminAPISchema>,
  { meta: ReferralAnalyticsMeta; people: ReferralAnalyticsPerson[] }
>;

/**
 * `GET /referral/analytics/admin-timeline` — one person's recent
 * events (what they looked at, which app, which code was current at
 * the time), newest first.
 */
export const referralAnalyticsTimelineAdminAPISchema = z.object({
  personId: z.string().trim().min(1).max(64).describe('PostHog person id.'),
  days: referralAnalyticsDaysSchema,
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type ReferralAnalyticsTimelineEvent = {
  event: string;
  timestamp: string;
  url?: string;
  app?: string;
  platform?: string;
  /** `referral_code_current` on the event, when different from the original. */
  codeCurrent?: string;
};
export type ReferralAnalyticsTimelineAdminAPI = API<
  z.infer<typeof referralAnalyticsTimelineAdminAPISchema>,
  { configured: boolean; events: ReferralAnalyticsTimelineEvent[] }
>;
